/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, VisuallyHidden } from '@coursera/cds-core';

import ProductCardCollectionPlaceholder from 'bundles/collections-common/components/ProductCardCollectionPlaceholder';
import type { Props } from 'bundles/page-config-common/components/ConsumerComponentsConfigRenderer';
import { ENTERPRISE_PAGE_CONFIG_COMPONENT_LIMIT } from 'bundles/page-config-common/constants/pagination';
import useComponentConfigPagination from 'bundles/page-config-common/hooks/useComponentConfigPagination';
import GroupedComponentsWrapperRenderer from 'bundles/page-config-common/module-renderers/GroupedComponentsWrapperRenderer';
import SkillFocusModuleRenderer from 'bundles/page-config-common/module-renderers/SkillFocusModuleRenderer';
import EmbeddedPromoTemplatePageModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EmbeddedPromoTemplatePageModuleRenderer';
import EnterpriseAssignedCoursesModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseAssignedCoursesModuleRenderer';
import EnterpriseBrowseCollectionModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseBrowseCollectionModuleRenderer';
import EnterpriseContinueLearningModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseContinueLearningModuleRenderer';
import EnterpriseCuratedCollectionModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseCuratedCollectionModuleRenderer';
import EnterpriseDomainPillsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseDomainPillsModuleRenderer';
import EnterpriseForCreditModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseForCreditModuleRenderer';
import EnterpriseLearningPathsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseLearningPathsModuleRenderer';
import EnterpriseLevelSetsCardsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseLevelSetsCardsModuleRenderer';
import EnterpriseOnboardingResultsHeaderModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseOnboardingResultsHeaderModuleRenderer';
import EnterpriseSkillClipsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseSkillClipsModuleRenderer';
import EnterpriseSkillSetCardsModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/EnterpriseSkillSetCardsModuleRenderer';
import GoogleProgramBannerModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/GoogleProgramBannerModuleRenderer';
import GoogleProgramFooterModuleRenderer from 'bundles/page-config-common/module-renderers/enterprise/GoogleProgramFooterModuleRenderer';
import type { PageConfig_GroupedComponentsWrapperMetadataFragment as GroupedComponentsWrapper } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';

import _t from 'i18n!nls/consumer-home';

/**
 * @description Default components config renderer for an enterprise context page.
 * Create a new config renderer component if your page needs to interpret components differently.
 */
const EnterpriseComponentsConfigRenderer = ({ pageConfigComponents, disablePagination }: Props) => {
  const [targetElementRef, isPaginationLoading] = useComponentConfigPagination(
    ENTERPRISE_PAGE_CONFIG_COMPONENT_LIMIT,
    disablePagination
  );

  if (!pageConfigComponents || !pageConfigComponents.length) {
    return null;
  }

  return (
    <React.Fragment>
      {pageConfigComponents.map((component) => {
        switch (component?.__typename) {
          case 'PageConfig_GroupedComponentsWrapper':
            return (
              <GroupedComponentsWrapperRenderer
                key={component.id}
                pageConfigComponent={component as GroupedComponentsWrapper}
                ConfigRenderer={EnterpriseComponentsConfigRenderer}
              />
            );
          case 'PageConfig_OnboardingResultsHeaderComponent':
            return <EnterpriseOnboardingResultsHeaderModuleRenderer key={component.id} />;
          case 'PageConfig_EnrolledProductsComponent':
            return <EnterpriseContinueLearningModuleRenderer key={component.id} pageConfigComponent={component} />;
          case 'PageConfig_DomainPillsComponent':
            return <EnterpriseDomainPillsModuleRenderer key={component.id} />;
          case 'PageConfig_EmbeddedPromoTemplatePage':
            return <EmbeddedPromoTemplatePageModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseForCreditCollectionComponent':
            return <EnterpriseForCreditModuleRenderer key={component.id} pageConfigComponent={component} />;
          case 'PageConfig_EnterpriseBrowseCollectionComponent':
            return <EnterpriseBrowseCollectionModuleRenderer key={component.id} pageConfigComponent={component} />;
          case 'PageConfig_EnterpriseAssignedCourses':
            return <EnterpriseAssignedCoursesModuleRenderer key={component.id} />;
          case 'PageConfig_GoogleProgramBanner':
            return <GoogleProgramBannerModuleRenderer key={component.id} />;
          case 'PageConfig_GoogleProgramFooter':
            return <GoogleProgramFooterModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseSkillClipsCollectionComponent':
            return <EnterpriseSkillClipsModuleRenderer key={component.id} />;
          case 'PageConfig_EnterpriseCuratedCollectionComponent':
            return <EnterpriseCuratedCollectionModuleRenderer key={component.id} pageConfigComponent={component} />;
          case 'PageConfig_EnterpriseLearningPaths':
            return <EnterpriseLearningPathsModuleRenderer key={component.id} />;
          case 'PageConfig_SkillSetCardsComponent':
            return <EnterpriseSkillSetCardsModuleRenderer key={component.id} />;
          case 'PageConfig_SkillsFocusComponent':
            return <SkillFocusModuleRenderer key={component.id} />;
          case 'PageConfig_LevelSets':
            return <EnterpriseLevelSetsCardsModuleRenderer key={component.id} />;
          // @ts-expect-error Testing purposes only
          case 'PageConfig_SandboxTestComponent':
            return <div key="PageConfig_SandboxTestComponent">SANDBOX_TEST_COMPONENT</div>;
          default:
            return null;
        }
      })}
      {/* Used for pagination tracking. */}
      {!disablePagination && <div ref={targetElementRef} />}
      {isPaginationLoading && !disablePagination && (
        <div aria-live="polite" aria-busy="true" data-testid="page-config-modules-loading">
          <VisuallyHidden>{_t('Loading more content')}</VisuallyHidden>
          <PageGridContainer>
            <Grid item xs={12}>
              <ProductCardCollectionPlaceholder />
            </Grid>
          </PageGridContainer>
        </div>
      )}
    </React.Fragment>
  );
};

export default EnterpriseComponentsConfigRenderer;
